import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ExternalLink, Lock, Target, UserPlus } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { PLANS_BY_LANG, tr } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import {
  useCheckoutConsents,
  CheckoutConsentCheckboxes,
  checkoutPayload,
} from "@/components/CheckoutConsent";
import { UK_AGENT_TEMPLATE } from "@/constants/ukAgentTemplate";
import { GET_MORE_AUDITIONS_TEMPLATE } from "@/constants/getMoreAuditionsTemplate";
import { MORE_CASTINGS_PL_TEMPLATE } from "@/constants/moreCastingsPLTemplate";

/**
 * Free-preview page for the 3 paid Career Paths (uk_agent, get_more_auditions,
 * more_castings_pl). Renders the full FREE-user preview up to the per-template
 * cutoff, then a hard-cut divider and the existing paywall CTA (START / MONTHLY
 * / ANNUAL). No goal is created for the FREE user — the page is 100% static
 * content plus the existing checkout flow.
 *
 * FREE preview cutoffs (defined by the user):
 *   uk_agent           → intro + legal section + Steps 01, 02, 03 (full)
 *   get_more_auditions → intro + Steps 01, 02, 03, 04 (full)
 *   more_castings_pl   → intro + Steps 01, 02, 03 (full) + Step 04
 *                        (intro/Punkt 1/Punkt 2 only; Google Alerts and
 *                         later Step 04 content, and Steps 05–11, are locked)
 */

const CTA = {
  uk_agent: {
    pl: {
      title: "Znajdź właściwego agenta w UK",
      sub: "Odblokuj pełną ścieżkę i przejdź przez cały proces krok po kroku.",
    },
    en: {
      title: "Find the Right Agent in the UK",
      sub: "Unlock the full Career Path and go through the entire process step by step.",
    },
  },
  get_more_auditions: {
    pl: {
      title: "Zwiększ swoje szanse na więcej castingów",
      sub: "Odblokuj pełną ścieżkę i zacznij działać krok po kroku.",
    },
    en: {
      title: "Increase Your Chances of Getting More Auditions",
      sub: "Unlock the full Career Path and start taking action step by step.",
    },
  },
  more_castings_pl: {
    pl: {
      title: "Zwiększ swoje szanse na castingi w Polsce",
      sub: "Odblokuj pełną ścieżkę i przejdź przez 30 dni działań krok po kroku.",
    },
    en: {
      title: "Zwiększ swoje szanse na castingi w Polsce",
      sub: "Odblokuj pełną ścieżkę i przejdź przez 30 dni działań krok po kroku.",
    },
  },
  mastermind: {
    pl: {
      title: "MASTERMIND: CASTING WYGRANY / NIEWYGRANY?",
      sub: "Odblokuj MASTERMIND i przejdź przez casting jak zawodowiec — od zatrzymania i refleksji po konkretne wyzwanie na następny casting.",
    },
    en: {
      title: "MASTERMIND: BOOKED IT / DIDN'T BOOK IT?",
      sub: "Unlock MASTERMIND and process your audition like a pro — from reflection and perspective to a clear challenge for your next audition.",
    },
  },
};

const UNLOCK_PLAN_IDS = ["leo_start", "leo_monthly", "leo_light", "leo_annual"];

const LABELS = {
  pl: {
    back: "Wróć",
    eyebrow: "Ścieżka kariery",
    unlock_heading: "Odblokuj pełną ścieżkę",
    choose_prefix: "Wybierz",
    opening: "Otwieram Stripe…",
    paywall_close: "Zamknij",
    fail: "Nie udało się utworzyć płatności.",
    locked_ahead: "Dalsza część ścieżki dostępna w planie płatnym",
    hard_cut_uk_agent: "Krok 04 „Znajdź konkretnego agenta” oraz Kroki 05–10 są dostępne w płatnym planie.",
    hard_cut_gma: "Krok 05 „Zwiększ liczbę trafnych zgłoszeń” oraz Kroki 06–10 są dostępne w płatnym planie.",
    hard_cut_mcpl: "Odblokuj pełną ścieżkę i zobacz krok po kroku, gdzie znajdować informacje o nowych produkcjach i jak je zamieniać w konkretne działania zawodowe.",
  },
  en: {
    back: "Back",
    eyebrow: "Career Path",
    unlock_heading: "Unlock the full Career Path",
    choose_prefix: "Choose",
    opening: "Opening Stripe…",
    paywall_close: "Close",
    fail: "Could not create payment.",
    locked_ahead: "The rest of this Career Path is available on a paid plan",
    hard_cut_uk_agent: "Step 04 \"Find the right agent\" and Steps 05–10 are available on a paid plan.",
    hard_cut_gma: "Step 05 \"Increase the number of relevant submissions\" and Steps 06–10 are available on a paid plan.",
    hard_cut_mcpl: "Odblokuj pełną ścieżkę i zobacz krok po kroku, gdzie znajdować informacje o nowych produkcjach i jak je zamieniać w konkretne działania zawodowe.",
  },
};

// ---------- Shared visual helpers ---------------------------------------

function StepHeader({ label, num, title, testid }) {
  return (
    <>
      <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase" data-testid={testid}>
        {label} {String(num).padStart(2, "0")}
      </div>
      <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">{title}</h2>
    </>
  );
}

function BodyText({ text }) {
  return <div className="text-stone-300 leading-relaxed mt-4 whitespace-pre-line">{text}</div>;
}

function EffectCallout({ text }) {
  return (
    <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm whitespace-pre-line">
      {text}
    </div>
  );
}

function StaticFieldsList({ fields }) {
  return (
    <ul className="mt-5 space-y-3">
      {fields.map((f, i) => (
        <li key={i} className="paper p-4">
          <div className="serif text-lg text-stone-100">{f.label}</div>
          {f.hint && <div className="text-stone-400 text-sm mt-1">{f.hint}</div>}
        </li>
      ))}
    </ul>
  );
}

function StaticChecklistWithHints({ items }) {
  return (
    <ul className="mt-5 space-y-2">
      {items.map((c, i) => (
        <li key={i} className="paper p-3 flex items-start gap-3">
          <span className="w-4 h-4 rounded-sm border border-amber-500/50 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-stone-200 text-sm">{c.label}</div>
            {c.hint && <div className="text-stone-400 text-xs mt-1 whitespace-pre-line">{c.hint}</div>}
            {c.callout && (
              <div className="mt-2 paper p-3 border-amber-500/30 bg-amber-500/5">
                <div className="serif text-sm text-amber-100">{c.callout.title}</div>
                <div className="text-stone-300 text-xs mt-1 whitespace-pre-line">{c.callout.body}</div>
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function StaticChecklist({ items }) {
  return (
    <ul className="mt-5 space-y-2">
      {items.map((label, i) => (
        <li key={i} className="paper p-3 flex items-start gap-3">
          <span className="w-4 h-4 rounded-sm border border-amber-500/50 mt-0.5 shrink-0" />
          <span className="text-sm text-stone-200">{label}</span>
        </li>
      ))}
    </ul>
  );
}

function AutoLinkText({ text }) {
  return (
    <>
      {text.split(/(https?:\/\/[^\s]+)/g).map((chunk, i) =>
        /^https?:\/\//.test(chunk) ? (
          <a key={i} href={chunk} target="_blank" rel="noopener noreferrer"
            className="text-amber-400 underline hover:text-amber-300 break-all">
            {chunk}
          </a>
        ) : (
          <span key={i}>{chunk}</span>
        )
      )}
    </>
  );
}

// ---------- UK Agent free preview ---------------------------------------

function UkAgentFreePreview({ L, lang, paywallNode, hasAccess }) {
  const [prefill, setPrefill] = useState(null);
  const [openSections, setOpenSections] = useState(() => new Set(["A"]));
  const toggleSection = (key) => setOpenSections((prev) => {
    const s = new Set(prev);
    if (s.has(key)) s.delete(key); else s.add(key);
    return s;
  });
  const openAddToCrm = (agency) => setPrefill({
    first_name: agency.name, last_name: "", type: "agent_uk", company: "",
    email: agency.email || "", project_context: agency.website || "",
    notes: agency.website ? (lang === "pl" ? `Strona: ${agency.website}` : `Website: ${agency.website}`) : "",
  });

  const previewSteps = L.steps.slice(0, 3); // Steps 01, 02, 03

  return (
    <>
      {/* Legal work in UK section */}
      {L.preSection && (
        <section className="mt-10 paper p-6 border-amber-500/25" data-testid="uk-agent-pre-section">
          <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">Info</div>
          <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">{L.preSection.title}</h2>
          {L.preSection.linkHref && (
            <a href={L.preSection.linkHref} target="_blank" rel="noopener noreferrer"
              className="mono text-xs text-amber-500 hover:text-amber-400 mt-3 inline-flex items-center gap-1">
              <ExternalLink size={12} /> {L.preSection.linkLabel}
            </a>
          )}
          <div className="mt-5 space-y-4">
            {L.preSection.blocks.map((b, i) => (
              <div key={i}>
                {b.heading && <h3 className="serif text-lg text-amber-100">{b.heading}</h3>}
                <p className="text-stone-300 text-sm leading-relaxed mt-2 whitespace-pre-line">{b.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 space-y-12 max-w-4xl">
        {previewSteps.map((step, idx) => (
          <motion.section
            key={idx}
            data-testid={`uk-agent-preview-step-${idx + 1}`}
            initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.3 }}
          >
            <StepHeader label={L.stepLabel} num={idx + 1} title={step.title} />
            {step.body && <BodyText text={step.body} />}
            {step.fields && <StaticFieldsList fields={step.fields} />}
            {step.checklist && <StaticChecklistWithHints items={step.checklist} />}
            {step.effect && <EffectCallout text={step.effect} />}
            {idx === 2 && paywallNode}
            {(idx !== 2 || hasAccess) && step.sections && step.sections.map((sec) => (
              <div key={sec.key} className="mt-6">
                <button
                  data-testid={`uk-agent-preview-section-${sec.key}`}
                  onClick={() => toggleSection(sec.key)}
                  className="w-full paper p-4 text-left flex items-center justify-between hover:border-amber-500/50"
                >
                  <span className="serif text-xl text-stone-100">{sec.title}</span>
                  <span className="mono text-[10px] uppercase tracking-widest text-amber-500">
                    {openSections.has(sec.key) ? L.collapse : L.expand}
                  </span>
                </button>
                {openSections.has(sec.key) && (
                  <ul className="mt-3 space-y-2">
                    {sec.agencies.map((a, i) => (
                      <li key={i} className="paper p-4">
                        <div className="serif text-lg text-stone-100">{a.name}</div>
                        {a.website && <div className="mono text-xs text-stone-400 mt-1 break-all">{a.website}</div>}
                        {a.email ? (
                          <div className="mono text-xs text-amber-500/80 mt-1 break-all">{a.email}</div>
                        ) : (
                          <div className="mono text-[11px] text-stone-500 mt-1 italic">{L.noEmailNote}</div>
                        )}
                        {a.note && <div className="text-stone-300 text-sm mt-2">{a.note}</div>}
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {a.website && (
                            <a
                              href={`https://${a.website}`}
                              target="_blank" rel="noopener noreferrer"
                              className="ghost-btn !py-2 !text-xs"
                            >
                              <ExternalLink size={12} /> {L.openWebsite}
                            </a>
                          )}
                          <button
                            data-testid={`uk-agent-preview-add-${a.name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                            onClick={() => openAddToCrm(a)}
                            className="pill-btn !py-2 !text-xs"
                          >
                            <UserPlus size={12} /> {L.addToCrm}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            {(idx !== 2 || hasAccess) && step.underListNote && (
              <div className="mt-6 text-stone-300 leading-relaxed whitespace-pre-line">
                {step.underListNote}
              </div>
            )}
            {(idx !== 2 || hasAccess) && step.moreLinks && (
              <div className="mt-4">
                <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase mb-2">
                  {step.moreLinksHeading}
                </div>
                <ul className="space-y-2">
                  {step.moreLinks.map((m, i) => (
                    <li key={i} className="paper p-3">
                      <div className="text-stone-100 text-sm">{m.name}</div>
                      <a
                        href={`https://${m.website.replace(/^https?:\/\//, "")}`}
                        target="_blank" rel="noopener noreferrer"
                        className="mono text-xs text-amber-500 hover:text-amber-400 break-all"
                      >
                        {m.website}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.section>
        ))}
      </div>

      <ContactFormDialog
        open={prefill !== null}
        onOpenChange={(v) => { if (!v) setPrefill(null); }}
        prefill={prefill}
        onSaved={() => setPrefill(null)}
      />
    </>
  );
}

// ---------- Get More Auditions free preview -----------------------------

function GetMoreAuditionsFreePreview({ L }) {
  const previewSteps = L.steps.slice(0, 4); // Steps 01–04
  return (
    <div className="mt-10 space-y-12 max-w-4xl">
      {previewSteps.map((step, idx) => (
        <motion.section
          key={idx}
          data-testid={`gma-preview-step-${idx + 1}`}
          initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.3 }}
        >
          <StepHeader label={L.stepLabel} num={idx + 1} title={step.title} />
          {step.body && <BodyText text={step.body} />}

          {step.numericFields && (
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {step.numericFields.map((f) => (
                <li key={f.key} className="paper p-4">
                  <div className="serif text-base text-stone-100">{f.label}</div>
                </li>
              ))}
            </ul>
          )}

          {step.guidance && (
            <ul className="mt-5 space-y-2">
              {step.guidance.map((g, i) => (
                <li key={i} className="paper p-3 text-stone-200 text-sm leading-relaxed">{g}</li>
              ))}
            </ul>
          )}

          {step.fields && (
            <ul className="mt-5 space-y-3">
              {step.fields.map((f) => (
                <li key={f.key} className="paper p-4">
                  <div className="serif text-base text-stone-100">{f.label}</div>
                  {f.hint && <div className="text-stone-400 text-sm mt-1">{f.hint}</div>}
                </li>
              ))}
            </ul>
          )}

          {step.checklist && <StaticChecklist items={step.checklist} />}

          {step.after && (
            <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm leading-relaxed whitespace-pre-line">
              {step.after}
            </div>
          )}

          {step.effect && <EffectCallout text={step.effect} />}

          {step.subsections && (
            <div className="mt-5 space-y-4">
              {step.subsections.map((s, i) => (
                <div key={i} className="paper p-4">
                  <h3 className="serif text-lg text-amber-100">{s.heading}</h3>
                  <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line mt-2">{s.body}</p>
                </div>
              ))}
            </div>
          )}
        </motion.section>
      ))}
    </div>
  );
}

// ---------- More Castings PL free preview -------------------------------

// Truncate Step 04 body at the first sentence — FREE user sees only the
// opening hook. Everything from that point on (finansowanie, PISF, fundusze,
// Film Commissions, radar, Google Alerts, kolejne kroki researchu) is locked
// behind the paywall.
function truncateMcplStep4Body(body) {
  if (!body) return "";
  const marker = "Nie czekaj wyłącznie na ogłoszenia castingowe.";
  const idx = body.indexOf(marker);
  if (idx === -1) return body.split(/\n\n/)[0] || body;
  return body.slice(0, idx + marker.length);
}

function MoreCastingsPLFreePreview({ L, hasAccess }) {
  const nav = useNavigate();
  // Entitled users never stay on the preview — send them to the full Career Path.
  useEffect(() => { if (hasAccess) nav("/career?view=plan", { replace: true }); }, [hasAccess, nav]);
  const previewSteps = L.steps.slice(0, 3); // Steps 01–03
  const step4 = L.steps[3]; // Step 04 partial
  return (
    <div className="mt-10 space-y-12 max-w-4xl">
      {previewSteps.map((step, idx) => (
        <motion.section
          key={idx}
          data-testid={`mcpl-preview-step-${idx + 1}`}
          initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.3 }}
        >
          <StepHeader label={L.stepLabel} num={idx + 1} title={step.title} />
          {step.body && (
            <div className="text-stone-300 leading-relaxed mt-4 whitespace-pre-line">
              <AutoLinkText text={step.body} />
            </div>
          )}
          {step.checklist && <StaticChecklist items={step.checklist} />}
          {step.reflection && (
            <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm italic">
              {step.reflection}
            </div>
          )}
          {step.subsection && (
            <div className="mt-6 paper p-5 border-amber-500/25">
              <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">Sekcja</div>
              <h3 className="serif text-xl text-stone-100 mt-1">{step.subsection.heading}</h3>
              {step.subsection.body && (
                <p className="text-stone-300 text-sm leading-relaxed mt-3 whitespace-pre-line">
                  {step.subsection.body}
                </p>
              )}
              {step.subsection.checklist && <StaticChecklist items={step.subsection.checklist} />}
              {step.subsection.after && (
                <p className="text-stone-300 text-sm leading-relaxed mt-4 whitespace-pre-line">
                  {step.subsection.after}
                </p>
              )}
            </div>
          )}
        </motion.section>
      ))}

      {/* Step 04 — partial (intro + Punkt 1 + Punkt 2 only) */}
      {step4 && (
        <motion.section
          data-testid="mcpl-preview-step-4-partial"
          initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }} transition={{ duration: 0.3 }}
        >
          <StepHeader label={L.stepLabel} num={4} title={step4.title} />
          {step4.body && (
            <div className="text-stone-300 leading-relaxed mt-4 whitespace-pre-line">
              <AutoLinkText text={truncateMcplStep4Body(step4.body)} />
            </div>
          )}
          {/* Collapsibles (Regionalne fundusze, Film Commissions, Google Alerts)
              are ALL locked behind the paywall for FREE users. */}
        </motion.section>
      )}
    </div>
  );
}

// ---------- Main page ---------------------------------------------------

export default function CareerPathPreview() {
  const { template } = useParams();
  const { lang, user, access } = useAuth();
  const nav = useNavigate();
  const t = (k) => tr(k, lang);
  const [busy, setBusy] = useState(null);
  const { consentOf, setConsent, canBuy } = useCheckoutConsents();
  const selectedRef = useRef(null);
  const startedRef = useRef(false);
  const viewedRef = useRef(false);

  const validTemplate = template === "uk_agent" || template === "get_more_auditions" || template === "more_castings_pl" || template === "mastermind";
  const L = template === "mastermind"
    ? { title: CTA.mastermind[lang === "en" ? "en" : "pl"].title }
    : template === "uk_agent"
    ? UK_AGENT_TEMPLATE[lang === "en" ? "en" : "pl"]
    : template === "more_castings_pl"
    ? MORE_CASTINGS_PL_TEMPLATE.pl
    : GET_MORE_AUDITIONS_TEMPLATE[lang === "en" ? "en" : "pl"];
  const cta = validTemplate ? CTA[template][lang === "en" ? "en" : "pl"] : null;
  const labels = LABELS[lang === "en" ? "en" : "pl"];
  const plans = PLANS_BY_LANG[lang] || PLANS_BY_LANG.pl;
  const unlockPlans = UNLOCK_PLAN_IDS
    .map((id) => plans.find((p) => p.id === id))
    .filter(Boolean);

  useEffect(() => {
    if (!validTemplate) return;
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackEvent("paywall_viewed", { kind: "career_paths", source: template });
    return () => {
      if (startedRef.current) {
        trackEvent("checkout_abandoned", {
          kind: "career_paths", source: template, plan: selectedRef.current,
        });
      }
    };
    // eslint-disable-next-line
  }, [validTemplate]);

  if (!validTemplate) {
    nav("/career", { replace: true });
    return null;
  }

  const buy = async (planId, plan) => {
    if (!user) { nav("/login"); return; }
    const suffix = lang === "pl" ? "_pln" : "_usd";
    const effective_key = `${planId}${suffix}`;
    const currency = lang === "pl" ? "PLN" : "USD";
    trackEvent("paywall_plan_selected", {
      kind: "career_paths", source: template, plan: planId,
      price: plan.price, currency,
    });
    selectedRef.current = planId;
    setBusy(planId);
    try {
      trackEvent("checkout_started", {
        kind: "career_paths", source: template, plan: planId,
        price: plan.price, currency, lookup_key: effective_key,
      });
      startedRef.current = true;
      const r = await api.post("/payments/checkout", checkoutPayload(effective_key));
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || labels.fail);
      setBusy(null);
    }
  };

  const hasAccess = access?.has_access === true
    && ["start", "monthly", "annual", "light"].includes(access?.plan);

  // Intro shown above preview for the two templates that ship an intro.
  const introText = (template === "get_more_auditions" || template === "more_castings_pl") ? L.intro : (template === "uk_agent" ? L.salesDescription : null);

  const hardCutLabel = template === "uk_agent"
    ? labels.hard_cut_uk_agent
    : template === "get_more_auditions"
    ? labels.hard_cut_gma
    : labels.hard_cut_mcpl;

  // Existing paywall CTA extracted so it can be reused in two positions:
  //  - inline inside Step 03 of the uk_agent preview (target position)
  //  - at the page bottom for every other template (unchanged)
  const paywallSection = (
    <section className="mt-10 paper p-6 sm:p-8" data-testid="career-preview-paywall">
      <h3 className="serif text-2xl sm:text-3xl text-stone-100">{cta.title}</h3>
      <p className="text-stone-400 mt-3 text-sm leading-relaxed">{cta.sub}</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {unlockPlans.map((plan) => (
          <div
            key={plan.id}
            data-testid={`career-preview-plan-${plan.id}`}
            className="border border-stone-800 rounded-2xl p-5 bg-stone-900/60 flex flex-col"
          >
            <div className="serif text-xl text-stone-100">{plan.name}</div>
            <div className="mono text-xs uppercase tracking-widest text-amber-400 mt-2">
              {plan.price} <span className="text-stone-500">{plan.priceSuffix || ""}</span>
            </div>
            <CheckoutConsentCheckboxes
              lang={lang}
              planId={plan.id}
              consent={consentOf(plan.id)}
              onChange={(k, v) => setConsent(plan.id, k, v)}
              testidPrefix="career-preview-consent"
            />
            <div className="flex-1" />
            <button
              data-testid={`career-preview-buy-${plan.id}`}
              disabled={busy === plan.id || !canBuy(plan.id)}
              onClick={() => buy(plan.id, plan)}
              className="pill-btn mt-4 w-full disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === plan.id ? labels.opening : `${labels.choose_prefix} ${plan.name.replace(/^LEO\s+/i, "")}`}
            </button>
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div className="relative max-w-4xl" data-testid={`career-preview-${template}`}>
      <button
        onClick={() => nav("/career")}
        className="mono text-[10px] text-stone-500 hover:text-amber-500 uppercase tracking-widest flex items-center gap-1 mb-4"
        data-testid="career-preview-back-btn"
      >
        <ChevronLeft size={11} /> {labels.back}
      </button>

      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
        <Target size={12} /> {labels.eyebrow}
      </div>
      <h1 className="serif text-4xl sm:text-5xl text-stone-100 mt-2">{L.title}</h1>

      {introText && (
        <div className="paper p-6 border-amber-500/30 bg-amber-500/5 mt-6">
          <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line">
            {introText}
          </p>
        </div>
      )}

      {/* Full FREE preview per template */}
      {template === "uk_agent" && <UkAgentFreePreview L={L} lang={lang} paywallNode={paywallSection} hasAccess={hasAccess} />}
      {template === "get_more_auditions" && <GetMoreAuditionsFreePreview L={L} />}
      {template === "more_castings_pl" && <MoreCastingsPLFreePreview L={L} hasAccess={hasAccess} />}

      {/* Hard cut (skipped for mastermind — it has no free preview) */}
      {template !== "mastermind" && (
        <div
          data-testid="career-preview-hard-cut"
          className="mt-10 paper p-6 border-amber-500/40 bg-amber-500/5 text-amber-100 text-sm leading-relaxed flex items-start gap-3"
        >
          <Lock size={16} className="mt-0.5 shrink-0" />
          <span>{hardCutLabel}</span>
        </div>
      )}

      {/* CTA — existing paywall (bottom position for every template EXCEPT
          uk_agent, where the same JSX is rendered inline inside Step 03) */}
      {template !== "uk_agent" && paywallSection}
    </div>
  );
}
